import "bootstrap/dist/css/bootstrap.min.css";
import { AmendmentMarker } from "components/TaskTracker/AmendmentMarker";
import classNames from "classnames";
import { CustomChatSlider } from "components/CustomChatSlider/CustomChatSlider";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  EstimdatedTimeColumn,
  PreparerColumn,
  ReviewerColumn,
  ServiceTeamColumn,
  TaskNotes,
} from "components/tasks/taskForForeignCA/ForeignCATask";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useUnreadFromChannels } from "hooks/useUnreadFromChannels";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
  VisibilityState,
} from "react-table-8.10.7";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { PlusIcon } from "components/icons/PlusIcon";
import { Search } from "components/DesignSystem/Search/Search";
import { Task1120Tracker } from "types/Models/TaskTracker";
import { openLink } from "utils/openLink";
import { BookkeepingStatus } from "./Task1120Columns/BookkeepingStatus";
import { TaskGlobalStatus } from "./Task1120Columns/GlobalStatus";
import { NonUsSubsidiaryBooks } from "./Task1120Columns/NonUsSubsidiaryBooks";
import { NoOfSubsidiaries } from "./Task1120Columns/NoOfSubsidiaries";
import { RecentNote } from "./Task1120Columns/RecentNote";
import { StateRegistrations } from "./Task1120Columns/StateRegistrations";
import { TaskPickupDate } from "./Task1120Columns/TaskPickupDate";
import { Task1120Slider } from "./TaskSlider/Task1120Slider";
import { TaskStatusWithFiles } from "./Task1120Columns/TaskStatusWithFiles";
import { PrevYearFilingStatus } from "./Task1120Columns/PrevYearStatus";
import dayjs from "dayjs";
import { AssignAgentColumnHeader } from "./Task1120ColumnFilters/AssignAgentColumnHeader";
import { Task1120TrackerFilterType } from "./Task1120TrackerFilter";
import { BookkeepingStatusColumnHeader } from "./Task1120ColumnFilters/BookkeepingStatusColumnHeader";
import { ServiceTeamMemberColumnHeader } from "./Task1120ColumnFilters/ServiceTeamMemberColumnHeader";
import { ServiceTeamColumnHeader } from "./Task1120ColumnFilters/ServiceTeamColumnHeader";
import PickupDateColumnHeader from "./Task1120ColumnFilters/PickupDateColumnHeader";
import { ETDColumnHeader } from "./Task1120ColumnFilters/ETDColumnHeader";
import { TaskStatusColumnHeader } from "./Task1120ColumnFilters/TaskStatusColumnHeader";
import {
  FBAR_STATUS,
  FILTER_PREV_YEAR_FORM_1120_STATUS,
  FILTER_PREV_YEAR_FORM_5471_STATUS,
  FILTER_PREV_YEAR_FORM_5472_STATUS,
  FILTER_TASK_1120_STATUS,
  FILTER_TASK_5471_STATUS,
  FILTER_TASK_5472_STATUS,
  FILTER_TASK_7004_STATUS,
  FILTER_TASK_R_AND_D_STATUS,
  STATUS_UNPAID,
} from "./Task1120FilterOptions";
import { TaskStateRegistrationColumnHeader } from "./Task1120ColumnFilters/TaskStateRegistrationColumnHeader";
import PreparerAssignedOnColumnHeader from "./Task1120ColumnFilters/PreparerAssignedOnColumnHeader";
import { ReviewerColumnHeader } from "./Task1120ColumnFilters/ReviewerColumnHeader";
import { Badge } from "components/design/badge";
import {
  CRM_SUBSCRIPTION_BADGE_MAP,
  CRM_SUBSCRIPTION_TYPE,
} from "dictionaries";
import { useQuery } from "hooks/useQuery";
import { Task1120Loader } from "./Task1120Loader";
import { BookkeeperColumnHeader } from "./Task1120ColumnFilters/BookkeeperColumnHeader";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { UpdateFilterType } from "components/ReusableHeaderFilter/ColumnHeaderMultiSelectFilter";
import { ETDCalendarHeader } from "./Task1120ColumnFilters/ETDCalendarHeader";
import { formatDate } from "utils/formatDate";
import { Link } from "components/DesignSystem/Link/Link";
import { PriorityScore } from "./Task1120Columns/PriorityScore";
import { GlobalStatusColumnHeader } from "./Task1120ColumnFilters/GlobalStatusColumnHeader";
import { SubFilings } from "./Task1120Columns/SubFilings";
import { PreparationCompletedOnColumn } from "./Task1120Columns/PreparationCompletedOnColumn";
import { ReviewEndDateColumn } from "./Task1120Columns/ReviewEndDateColumn";
import { ReviewStartDateColumn } from "./Task1120Columns/ReviewStartDateColumn";
import { MissingInformationColumn } from "./Task1120Columns/MissingInformationColumn";
import { MiRaisedOnColumn } from "./Task1120Columns/MiRaisedOnColumn";
import { MiReceivedOnColumn } from "./Task1120Columns/MiReceivedOnColumn";
import { OnHoldReasonColumn } from "./Task1120Columns/OnHoldReasonColumn";
import Form1120CompletedOnColumnHeader from "./Task1120ColumnFilters/Form1120CompletedOnColumnHeader";

const FIXED_COLUMNS = new Set(["selection", "entity_and_group"]);
const columnHelper = createColumnHelper<Task1120Tracker>();

export const Task1120Table = ({
  task,
  perPage,
  values,
  updateFilter,
  isLoading,
  isSuccess,
  isEmpty,
  rowSelection,
  setRowSelection,
  selectedEntityIds,
  setSelectedEntityIds,
}: {
  task: Task1120Tracker[];
  perPage: number;
  isLoading: boolean;
  isSuccess: boolean;
  isEmpty: boolean;
  updateFilter: UpdateFilterType;
  rowSelection: Record<string, boolean>;
  setRowSelection: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  selectedEntityIds: string[];
  setSelectedEntityIds: React.Dispatch<React.SetStateAction<string[]>>;
} & Task1120TrackerFilterType) => {
  const SELECTION_COL_WIDTH = 46;
  const { isAdmin, isForeignCA } = useRoleBasedView();
  const query = useQuery();
  const selectedSeason = Number(
    query.get("season") || dayjs().subtract(1, "year").year()
  );
  const prevSeason = selectedSeason - 1;
  const [selectedTask, setSelectedTask] = useState<Task1120Tracker | null>(
    null
  );
  const taskDetailSlider = useModal();
  const [defaultTab, setDefaultTab] = useState<"DETAILS" | "CHAT">("DETAILS");

  //@ts-ignore
  const channelUrls: string[] = task
    .filter((t) => t.channel.channel_url !== null)
    .map((t) => t.channel.channel_url);

  const channelUnreadMap = useUnreadFromChannels({
    channelUrls,
    queryOptions: { limit: perPage },
  });

  const handleSliderClose = () => {
    setSelectedTask(null);
    taskDetailSlider.close();
  };

  const handleHeaderCheckboxChange = useCallback(() => {
    const selectableRows = task
      .filter((row) => !!row.task_uuid)
      .map((row) => row.entity.uuid);
    const allSelected = selectableRows.every((id) => rowSelection[id]);
    if (allSelected) {
      const newSelection = { ...rowSelection };
      selectableRows.forEach((id) => {
        delete newSelection[id];
      });
      setRowSelection(newSelection);
    } else {
      const newSelection = { ...rowSelection };
      selectableRows.forEach((id) => {
        newSelection[id] = true;
      });
      setRowSelection(newSelection);
    }
  }, [task, rowSelection, setRowSelection]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnSearch, setColumnSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "priority_score", desc: true },
  ]);

  const openTaskChat = useCallback(
    (chatTask: Task1120Tracker) => {
      setDefaultTab("CHAT");
      setSelectedTask(chatTask);
      taskDetailSlider.open();
    },
    [taskDetailSlider]
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "selection",
        size: 46,
        enableSorting: false,
        header: () => {
          const selectableRows = task
            .filter((row) => !!row.task_uuid)
            .map((row) => row.entity.uuid);
          const selectedCount = selectableRows.filter(
            (id) => rowSelection[id]
          ).length;
          const allSelected =
            selectableRows.length > 0 &&
            selectedCount === selectableRows.length;
          const indeterminate = selectedCount > 0 && !allSelected;
          return (
            <Checkbox
              checked={allSelected}
              indeterminate={indeterminate}
              onChange={handleHeaderCheckboxChange}
            />
          );
        },

        cell: ({ row }) => {
          const isDisabled = !row.getCanSelect();
          return (
            <ConditionalToolTip
              condition={
                isDisabled && "Task has not been launched for this entity"
              }
            >
              <span>
                <Checkbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: isDisabled,
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler(),
                    key: row.id,
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      columnHelper.accessor("entity.group.uuid", {
        cell: (info) => {
          const rowData = info.row?.original;
          const groupId = rowData?.entity.group.uuid;
          const groupName = rowData?.entity.group.name;
          const groupDetailsRoute = isForeignCA
            ? `/practice/crm/${groupId}`
            : `/crm/${groupId}`;

          const openCompany = (
            e: React.MouseEvent<HTMLButtonElement, MouseEvent>
          ) => {
            e.stopPropagation();
            rowData?.has_group_perm && openLink(groupDetailsRoute, groupId);
          };

          return (
            <div className="t-flex t-flex-col">
              <div className="t-flex t-gap-2 t-items-center">
                <Button
                  size="small"
                  customType={rowData?.has_group_perm ? "link" : "ghost"}
                  onClick={openCompany}
                >
                  <div className="t-truncate t-text-body-sm hover:t-underline">
                    {groupName}
                  </div>
                </Button>
                {Boolean(rowData?.entity?.group?.subscription_name) &&
                  rowData?.entity?.group?.subscription_name !==
                    "NOT_STARTED" && (
                    <Badge
                      size="small"
                      color={
                        CRM_SUBSCRIPTION_TYPE[
                          rowData?.entity?.group?.subscription_name
                        ]
                      }
                    >
                      {
                        CRM_SUBSCRIPTION_BADGE_MAP[
                          rowData?.entity?.group?.subscription_name
                        ]
                      }
                    </Badge>
                  )}
              </div>
              <div className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-30">
                <span className="t-truncate">{rowData.entity.name}</span>
                {rowData?.is_amendment && (
                  <>
                    <span>·</span>
                    <AmendmentMarker
                      amendmentNumber={rowData.amendment_number}
                      startedOn={rowData.amendment_started_on}
                      startedBy={rowData.amendment_started_by}
                    />
                  </>
                )}
              </div>
            </div>
          );
        },
        header: "ENTITY AND GROUP",
        size: 260,
        id: "entity_and_group",
      }),

      columnHelper.accessor("assigned_support_agent.uuid", {
        cell: ({ row }) => (
          <div className="t-text-body-sm t-text-text-60">
            {row.original?.assigned_support_agent?.name || "-"}
          </div>
        ),
        header: () => (
          <AssignAgentColumnHeader
            values={values}
            updateFilter={updateFilter}
          />
        ),
        meta: {
          showDivier: true,
          label: "Assigned Agent",
        },
        size: 180,
      }),
      columnHelper.accessor("task_1120_tracker_global_status", {
        cell: ({ row }) => (
          <TaskGlobalStatus
            taskData={{
              ...row.original,
              uuid: row.original.task_uuid,
            }}
          />
        ),
        header: () => (
          <GlobalStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
          />
        ),
        meta: {
          label: "Global Status",
        },
        size: 257,
      }),
      columnHelper.accessor("priority_score", {
        header: "Priority Score",
        cell: ({ row }) => <PriorityScore row={row.original} />,
        size: 120,
        enableSorting: true,
        sortUndefined: -1,
        sortDescFirst: true,
      }),
      ...(isAdmin
        ? [
            columnHelper.accessor("assigned_team", {
              cell: ({ row }) => (
                <ServiceTeamColumn
                  taskData={{ ...row.original, uuid: row.original.task_uuid }}
                />
              ),
              header: () => (
                <ServiceTeamColumnHeader
                  values={values}
                  updateFilter={updateFilter}
                />
              ),
              meta: {
                showDivider: true,
                label: "Service Team",
              },
              size: 150,
            }),
          ]
        : []),
      columnHelper.display({
        id: "service_team_assigned_on",
        header: "Tax Team Assigned On",
        cell: ({ row }) => {
          const date = row.original.service_team_assigned_on;
          return <div>{date ? formatDate(date) : "-"}</div>;
        },
        size: 160,
      }),
      columnHelper.accessor("estimated_time_of_delivery", {
        cell: ({ row }) => (
          <EstimdatedTimeColumn
            taskData={{
              ...row.original,
              uuid: row.original.task_uuid,
              title: row.original.task.title,
            }}
            isCustomTrigger
          />
        ),
        header: () => (
          <ETDCalendarHeader values={values} updateFilter={updateFilter} />
        ),
        meta: {
          showDivider: true,
          label: "ETD",
        },
        size: 140,
      }),
      columnHelper.display({
        id: "task_notes",
        cell: ({ row }) => (
          <TaskNotes
            taskId={row.original.task_uuid}
            groupId={row.original.entity.group.uuid}
            entityId={row.original.entity.uuid}
            currentNoteLabel="Notes for this Task"
            allNotesCount={row.original.notes_count ?? 0}
          />
        ),
        header: "Notes",
        size: 80,
      }),
      columnHelper.accessor("assigned_to.uuid", {
        cell: ({ row }) => (
          <PreparerColumn
            taskData={{ ...row.original, uuid: row.original.task_uuid }}
            season={selectedSeason}
            suggestPreparer
          />
        ),
        meta: {
          showDivider: true,
          label: "Tax Preparer",
        },
        header: () => (
          <ServiceTeamMemberColumnHeader
            values={values}
            updateFilter={updateFilter}
            defaultServiceTeamId={task?.[0]?.assigned_team?.uuid || null}
          />
        ),
        size: 150,
      }),
      columnHelper.display({
        header: () => (
          <PreparerAssignedOnColumnHeader
            values={values}
            updateFilter={updateFilter}
          />
        ),
        meta: {
          label: "Preparer Assigned On",
        },
        id: "preparer_assigned_on",
        cell: ({ row }) => {
          const isDatePresent = !!row.original.preparer_assigned_on;

          if (isDatePresent) {
            return <div>{formatDate(row.original.preparer_assigned_on)}</div>;
          }
          return <div>-</div>;
        },
        size: 160,
      }),
      columnHelper.accessor("pickup_date", {
        cell: ({ row }) => (
          <TaskPickupDate
            hasAssignedTeam={Boolean(row.original.assigned_team)}
            taskId={row.original.task_uuid}
            pickupDate={row.original.pickup_date}
          />
        ),
        header: () => (
          <PickupDateColumnHeader values={values} updateFilter={updateFilter} />
        ),
        meta: {
          showDivider: true,
          label: "Pickup Date",
        },
        size: 140,
      }),
      columnHelper.display({
        id: "preparation_complete_date",
        header: "Preparation Completed On",
        cell: ({ row }) => <PreparationCompletedOnColumn row={row.original} />,
        size: 170,
      }),
      columnHelper.display({
        header: () => (
          <ReviewerColumnHeader
            values={values}
            updateFilter={updateFilter}
            defaultServiceTeamId={task?.[0]?.assigned_team?.uuid || null}
          />
        ),
        meta: {
          label: "Reviewer",
        },
        id: "reviewer",
        cell: ({ row }) => (
          <ReviewerColumn
            taskData={{ ...row.original, uuid: row.original.task_uuid }}
          />
        ),
        size: 150,
      }),
      columnHelper.display({
        header: "Review Start Date",
        meta: {
          label: "Review Start Date",
        },
        id: "reviewer_assigned_on",
        cell: ({ row }) => <ReviewStartDateColumn row={row.original} />,
        size: 160,
      }),
      columnHelper.display({
        header: "Review End Date",
        meta: {
          label: "Review End Date",
        },
        id: "review_end_date",
        cell: ({ row }) => <ReviewEndDateColumn row={row.original} />,
        size: 160,
      }),
      columnHelper.display({
        id: "missing_information",
        header: "Missing Information",
        cell: ({ row }) => <MissingInformationColumn row={row.original} />,
        size: 200,
      }),
      columnHelper.display({
        id: "mi_raised_on",
        header: "MI Raised On",
        cell: ({ row }) => <MiRaisedOnColumn row={row.original} />,
        size: 140,
      }),
      columnHelper.display({
        id: "mi_received_on",
        header: "MI Received On",
        cell: ({ row }) => <MiReceivedOnColumn row={row.original} />,
        size: 140,
      }),
      columnHelper.display({
        id: "on_hold_reason",
        header: "Reason for On Hold",
        cell: ({ row }) => <OnHoldReasonColumn row={row.original} />,
        size: 200,
      }),
      columnHelper.display({
        id: "wait_days",
        header: "Customer Waiting Days",
        cell: ({ row }) => {
          const days = row.original.ready_to_pick_since
            ? dayjs().diff(dayjs(row.original.ready_to_pick_since), "day")
            : null;
          return (
            <div className="t-text-body-sm t-text-text-60">
              {days !== null ? `${days}d` : "-"}
            </div>
          );
        },
        size: 160,
      }),
      columnHelper.accessor("task_1120_bookkeeping_status", {
        cell: ({ row }) => (
          <BookkeepingStatus
            bookkeepingStatus={row.original.task_1120_bookkeeping_status}
            groupId={row.original.entity.group.uuid}
          />
        ),
        header: () => (
          <BookkeepingStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
          />
        ),
        meta: {
          showDivider: true,
          label: "Bookkeeping Status",
        },
        size: 160,
      }),
      columnHelper.accessor("bookkeeper", {
        cell: ({ row }) => (
          <div className="t-text-body-sm t-text-text-60">
            {row.original?.bookkeeper?.name || "-"}
          </div>
        ),
        header: () => (
          <BookkeeperColumnHeader values={values} updateFilter={updateFilter} />
        ),
        meta: {
          showDivider: true,
          label: "Bookkeeper",
        },
        size: 150,
      }),
      columnHelper.display({
        id: "sub_filings",
        header: "Forms",
        cell: ({ row }) => <SubFilings row={row.original} />,
        size: 160,
      }),
      columnHelper.accessor("form_7004_status", {
        cell: ({ row }) => (
          <TaskStatusWithFiles
            taskList={row.original.form_7004_status}
            groupId={row.original.entity.group.uuid}
          />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_TASK_7004_STATUS}
            header="7004 Status"
          />
        ),
        meta: {
          showDivider: true,
          label: "7004 Status",
        },
        size: 150,
      }),
      columnHelper.accessor("form_1120_status", {
        cell: ({ row }) => (
          <TaskStatusWithFiles
            taskList={row.original.form_1120_status}
            groupId={row.original.entity.group.uuid}
            taskId={row.original.task_uuid}
          />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_TASK_1120_STATUS}
            header="1120 Status"
          />
        ),
        meta: {
          showDivider: true,
          label: "1120 Status",
        },
        size: 150,
      }),
      columnHelper.display({
        id: "form_1120_completed_on",
        header: () => (
          <Form1120CompletedOnColumnHeader
            values={values}
            updateFilter={updateFilter}
          />
        ),
        cell: ({ row }) => {
          const date = row.original.form_1120_completed_on;
          return <div>{date ? formatDate(date) : "-"}</div>;
        },
        meta: {
          showDivider: true,
          label: "1120 Completed On",
        },
        size: 160,
      }),
      columnHelper.accessor("form_5471_status", {
        cell: ({ row }) => (
          <PrevYearFilingStatus taskList={row.original.form_5471_status} />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_TASK_5471_STATUS}
            header="5471 Status"
          />
        ),
        meta: {
          showDivider: true,
          label: "5471 Status",
        },
        size: 150,
      }),
      columnHelper.accessor("form_5472_status", {
        cell: ({ row }) => (
          <PrevYearFilingStatus taskList={row.original.form_5472_status} />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_TASK_5472_STATUS}
            header="5472 Status"
          />
        ),
        meta: {
          showDivider: true,
          label: "5472 Status",
        },
        size: 150,
      }),
      columnHelper.accessor("form_fbar_status", {
        cell: ({ row }) => (
          <PrevYearFilingStatus taskList={row.original.form_fbar_status} />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FBAR_STATUS}
            header="FBAR Status"
            extraFilters={[{ label: "Unpaid", value: STATUS_UNPAID }]}
          />
        ),
        meta: {
          showDivider: true,
          label: "FBAR Status",
        },
        size: 150,
      }),
      columnHelper.accessor("form_r_and_d_status", {
        cell: ({ row }) => (
          <PrevYearFilingStatus taskList={row.original.form_r_and_d_status} />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_TASK_R_AND_D_STATUS}
            header="R&D Filing"
            extraFilters={[
              { label: "Awaiting from ADP", value: "AWAITING_FROM_ADP" },
              {
                label: "Awaiting from customer",
                value: "AWAITING_FROM_CUSTOMER",
              },
            ]}
          />
        ),
        meta: {
          showDivider: true,
          label: "R&D Filing",
        },
        size: 150,
      }),
      columnHelper.accessor("number_of_subsidiaries.uuid", {
        cell: ({ row }) => (
          <NoOfSubsidiaries
            subsidiaries={row.original.number_of_subsidiaries}
          />
        ),
        header: "No. of Subsidiaries",
        size: 160,
      }),
      columnHelper.accessor(
        "non_us_subsidiary_books.total_count_of_non_us_subsidiary_books",
        {
          cell: ({ row }) => {
            const nonUsSubsidiaryBooks = row.original.non_us_subsidiary_books;
            return (
              <NonUsSubsidiaryBooks
                totalNonUsSubsidiaryBooks={
                  nonUsSubsidiaryBooks.total_count_of_non_us_subsidiary_books
                }
                receivedNonUsSubsidiaryBooks={
                  nonUsSubsidiaryBooks.total_available_non_us_subsidiary_books
                }
                nonUsSubsidiaryBooks={
                  row.original.non_us_subsidiary_books
                    .non_us_subsidiary_books_statuses || []
                }
                groupName={row.original.entity.group.name}
                taskName={row.original.task.title}
              />
            );
          },
          header: "Non US Subsidiary Books",
          size: 180,
        }
      ),
      columnHelper.accessor("state_registrations", {
        cell: ({ row }) => (
          <StateRegistrations
            registedStates={row.original.state_registrations}
          />
        ),
        header: () => (
          <TaskStateRegistrationColumnHeader
            values={values}
            updateFilter={updateFilter}
          />
        ),
        meta: {
          showDivider: true,
          label: "State Registrations",
        },
        size: 160,
      }),
      columnHelper.accessor("previous_season_form_1120_status", {
        cell: ({ row }) => (
          <TaskStatusWithFiles
            taskList={row.original.previous_season_form_1120_status}
            groupId={row.original.entity.group.uuid}
          />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_PREV_YEAR_FORM_1120_STATUS}
            header={`1120 (${prevSeason})`}
          />
        ),
        meta: {
          showDivider: true,
          label: `1120 (${prevSeason})`,
        },
        size: 130,
      }),
      columnHelper.accessor("previous_season_form_5471_status", {
        cell: ({ row }) => (
          <PrevYearFilingStatus
            taskList={row.original.previous_season_form_5471_status}
          />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_PREV_YEAR_FORM_5471_STATUS}
            header={`5471 (${prevSeason})`}
          />
        ),
        meta: {
          showDivider: true,
          label: `5471 (${prevSeason})`,
        },
        size: 130,
      }),
      columnHelper.accessor("previous_season_form_5472_status", {
        cell: ({ row }) => (
          <PrevYearFilingStatus
            taskList={row.original.previous_season_form_5472_status}
          />
        ),
        header: () => (
          <TaskStatusColumnHeader
            values={values}
            updateFilter={updateFilter}
            name={FILTER_PREV_YEAR_FORM_5472_STATUS}
            header={`5472 (${prevSeason})`}
          />
        ),
        meta: {
          showDivider: true,
          label: `5472 (${prevSeason})`,
        },
        size: 130,
      }),
      columnHelper.accessor("notes", {
        cell: ({ row }) => <RecentNote lastNote={row.original.notes} />,
        header: "Recent Note",
        size: 200,
      }),
      columnHelper.accessor("channel.channel_url", {
        header: "",
        cell: ({ row }) => {
          const defaultChannelId = row.original.channel.channel_url;
          if (!defaultChannelId) {
            return null;
          }
          return (
            <CustomChatSlider
              openTaskChat={() => openTaskChat(row.original)}
              defaultChannelId={defaultChannelId}
              defaultUnreadCount={channelUnreadMap[defaultChannelId]}
              taskId={row.original.task_uuid}
            />
          );
        },
        size: 60,
        meta: {
          label: "Channel",
        },
      }),
    ],
    [
      rowSelection,
      task,
      values,
      isForeignCA,
      isAdmin,
      selectedSeason,
      prevSeason,
      channelUnreadMap,
      handleHeaderCheckboxChange,
      openTaskChat,
      updateFilter,
    ]
  );

  const onRowClick = (row: Row<Task1120Tracker>) => {
    setDefaultTab("DETAILS");
    setSelectedTask(row.original);
    taskDetailSlider.open();
  };

  const table = useReactTable({
    data: task,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      minSize: 1,
    },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    state: {
      rowSelection,
      columnVisibility,
      sorting,
    },
    getRowId: (row) => row.entity.uuid,
    enableSorting: false,
    enableRowSelection: (row) => Boolean(row.original.task_uuid),
    initialState: {
      columnPinning: {
        left: ["selection", "entity_and_group"],
      },
    },
  });

  useEffect(() => {
    const selectedIds = Object.entries(rowSelection)
      .filter(([_, isSelected]) => isSelected)
      .map(([uuid]) => uuid);

    setSelectedEntityIds(selectedIds);
  }, [rowSelection]);

  useEffect(() => {
    if (task.length > 0) {
      const newSelections: Record<string, boolean> = {};

      selectedEntityIds.forEach((id) => {
        newSelections[id] = true;
      });

      setRowSelection(newSelections);
    }
  }, [task]);

  return (
    <div className="t-h-full">
      <Async.Root
        {...{ isLoading, isSuccess, isEmpty: false }}
        customLoader={<Task1120Loader />}
      >
        <Async.Empty>
          <></>
        </Async.Empty>
        <Async.Success>
          <div className="t-overflow-x-auto t-h-full">
            <Table.Container sticky className="t-h-full t-mb-28">
              <Table.Content>
                <Table.Body>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row
                      key={headerGroup.id}
                      className="t-pl-0 t-sticky t-top-0 t-z-[11] t-bg-surface-lighter-grey t-border-b t-border-surface-light-grey"
                    >
                      {headerGroup.headers.map((header) => {
                        const isSelectionCol = header.column.id === "selection";
                        const isEntityGroupCol =
                          header.column.id === "entity_and_group";
                        const isPinned = header.column.getIsPinned();

                        return (
                          <Table.Cell
                            key={header.id}
                            style={{
                              minWidth: header.getSize(),
                              ...(isSelectionCol && {
                                position: "sticky",
                                left: 0,
                                width: SELECTION_COL_WIDTH,
                                maxWidth: SELECTION_COL_WIDTH,
                                zIndex: 30,
                              }),
                              ...(isEntityGroupCol && {
                                position: "sticky",
                                left: SELECTION_COL_WIDTH,
                                zIndex: 20,
                              }),
                            }}
                            className={classNames(
                              "t-text-subtext-sm t-py-3 t-px-1.5 t-uppercase t-text-text-30 task-1120-tracker-cell t-bg-surface-lighter-grey",
                              {
                                "!t-bg-surface-lighter-grey t-z-30":
                                  isSelectionCol,
                                "!t-bg-surface-lighter-grey t-z-20 t-drop-shadow-table-left-pin":
                                  isEntityGroupCol,
                                "t-z-20": !isPinned,
                              }
                            )}
                          >
                            <span className="t-flex t-justify-between t-items-center t-gap-1">
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                              <span className="t-flex t-items-center t-gap-1">
                                {header.column.getCanSort() && (
                                  <Table.HeadCellSort
                                    nextSortOrder={header.column.getNextSortingOrder()}
                                    isSorted={header.column.getIsSorted()}
                                  />
                                )}
                              </span>
                            </span>
                          </Table.Cell>
                        );
                      })}
                      <Table.Cell
                        style={{
                          position: "sticky",
                          right: 0,
                          zIndex: 20,
                          width: 36,
                          minWidth: 36,
                          backgroundColor: "white",
                        }}
                        className="t-border-l t-border-surface-light-grey"
                      >
                        <Dropdown.Root>
                          <Dropdown.Trigger asChild>
                            <Button customType="ghost_icon" size="xsmall">
                              <PlusIcon size="12" />
                            </Button>
                          </Dropdown.Trigger>
                          <Dropdown.Portal>
                            <Dropdown.Content align="end" className="t-w-72">
                              <div
                                className="t-border-b t-border-surface-light-grey t-p-2"
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <Search
                                  placeholder="Search columns"
                                  value={columnSearch}
                                  onChange={(e) =>
                                    setColumnSearch(e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="t-max-h-64 t-overflow-y-auto">
                                {table
                                  .getAllLeafColumns()
                                  .filter((col) => {
                                    if (FIXED_COLUMNS.has(col.id)) return false;
                                    const header = col.columnDef.header;
                                    const label: string =
                                      (col.columnDef.meta as any)?.label ??
                                      (typeof header === "string"
                                        ? header
                                        : col.id);
                                    return label
                                      .toLowerCase()
                                      .includes(columnSearch.toLowerCase());
                                  })
                                  .map((col) => {
                                    const header = col.columnDef.header;
                                    const label: string =
                                      (col.columnDef.meta as any)?.label ??
                                      (typeof header === "string"
                                        ? header
                                        : col.id);
                                    return (
                                      <Dropdown.Item
                                        key={col.id}
                                        onSelect={(e) => e.preventDefault()}
                                      >
                                        <Checkbox
                                          label={label}
                                          name={col.id}
                                          checked={col.getIsVisible()}
                                          onChange={() =>
                                            col.toggleVisibility()
                                          }
                                        />
                                      </Dropdown.Item>
                                    );
                                  })}
                              </div>
                            </Dropdown.Content>
                          </Dropdown.Portal>
                        </Dropdown.Root>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row
                      key={row.id}
                      className={
                        row.original.is_urgent ? "t-pl-0 !t-bg-red-0" : "t-pl-0"
                      }
                      onRowClick={() => onRowClick(row)}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const isSelectionCol = cell.column.id === "selection";
                        const isEntityGroupCol =
                          cell.column.id === "entity_and_group";
                        const isPinned = cell.column.getIsPinned();

                        return (
                          <Table.Cell
                            key={cell.id}
                            style={{
                              minWidth: cell.column.getSize(),
                              ...(isSelectionCol && {
                                position: "sticky",
                                left: 0,
                                width: SELECTION_COL_WIDTH,
                                maxWidth: SELECTION_COL_WIDTH,
                                zIndex: 5,
                              }),
                              ...(isEntityGroupCol && {
                                position: "sticky",
                                left: SELECTION_COL_WIDTH,
                                zIndex: 5,
                              }),
                            }}
                            className={classNames(
                              "t-py-3 t-px-1.5 task-1120-tracker-cell",
                              {
                                "t-bg-white group-hover:!t-bg-surface-lighter-grey":
                                  !!isPinned,
                                "t-drop-shadow-table-left-pin":
                                  isEntityGroupCol,
                                "!t-bg-red-0": row.original.is_urgent,
                              }
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Table.Cell>
                        );
                      })}
                      <Table.Cell
                        style={{
                          position: "sticky",
                          right: 0,
                          zIndex: 5,
                          width: 36,
                          minWidth: 36,
                        }}
                        className={classNames(
                          "t-border-l t-border-surface-light-grey",
                          {
                            "!t-bg-red-0": row.original.is_urgent,
                            "t-bg-white": !row.original.is_urgent,
                          }
                        )}
                      />
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          </div>
          {isEmpty && (
            <div className="t-flex t-items-center t-justify-center t-h-[300px]">
              <EmptyScreen text="No task found" className="!t-h-full" />
            </div>
          )}
        </Async.Success>
      </Async.Root>
      {selectedTask && (
        <Task1120Slider
          rowData={selectedTask}
          isOpen={taskDetailSlider.isOpen}
          onClose={handleSliderClose}
          defaultTab={defaultTab}
        />
      )}
    </div>
  );
};
